import Pressable from '@/components/motion/pressable';
import CopyablePhone from '@/components/copyable-phone';
import SelectTgStatus from '@/components/select-tg-status';
import type { IMyClient } from '@/entities/my-clients-entity';
import { ContainerIcon, HashIcon, LayoutGridIcon, PhoneIcon } from 'lucide-react';
import { memo, useMemo, type MouseEvent } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

const MyClientCard = memo(function MyClientCard({ client }: { client: IMyClient }) {
	const navigate = useNavigate();

	function handleCopy(e: MouseEvent, text: string) {
		e.stopPropagation();
		navigator.clipboard
			.writeText(text)
			.then(() => toast.success('Скопировано'))
			.catch(() => toast.error('Ошибка копирования'));
	}

	const phones = useMemo(() => client.phones?.filter(p => p.number) ?? [], [client.phones]);

	return (
		<Pressable
			role='button'
			tabIndex={0}
			className='relative p-2 bg-card rounded-md flex flex-col gap-1.5 border-border border border-l-2 overflow-hidden cursor-pointer active:bg-accent/30'
			style={{
				borderColor: client.takeStatus === 1 ? '#ad46ff' : undefined,
				borderLeftColor: client.takeStatus !== 1 ? (client.isOnline ? 'var(--chart-2)' : 'var(--chart-5)') : '#ad46ff',
				backgroundColor: client.takeStatus === 1 ? 'var(--card)' : undefined,
			}}
			onClick={() => navigate(`/my-client/${client.id}`)}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					navigate(`/my-client/${client.id}`);
				}
			}}
		>
			{client.takeStatus === 1 && (
				<div className='absolute inset-0 font-semibold flex items-center justify-center select-none bg-black/10 z-2' style={{ backdropFilter: 'blur(5px)' }}>
					На вбиве - {client?.takenFrom}
				</div>
			)}
			<div className='flex justify-between '>
				<span className='flex gap-1 items-center break-all'>
					<div className='w-[18px]'>
						<HashIcon size={16} className='opacity-40' />
					</div>
					<span className='text-sm hover:bg-accent cursor-pointer select-none' onClick={e => handleCopy(e, '#' + client.id)}>
						{client.id}
					</span>
				</span>
				<span className='text-sm pl-2'>
					{client.isOnline ? (
						<span className='text-chart-2 flex items-center gap-1'>
							<div className='bg-chart-2 w-2 h-2 animate-pulse rounded-full'></div>Онлайн
						</span>
					) : (
						<span className='text-chart-5'>Офлайн</span>
					)}
				</span>
			</div>
			<div className='flex justify-between'>
				<span className='flex gap-1 items-center'>
					<div className='w-[18px]'>
						<PhoneIcon size={16} className='opacity-40' />
					</div>
					<div>
						{phones.length ? (
							phones.map((phone, i) => {
								if (!phone.number) return null;
								return (
									<CopyablePhone
										key={`${phone.subId}${i}`}
										value={phone.number}
										onCopy={text => {
											navigator.clipboard
												.writeText(text)
												.then(() => toast.success('Скопировано'))
												.catch(() => toast.error('Ошибка копирования'));
										}}
										className='mr-1 inline text-sm break-all rounded-xs px-0.5'
									/>
								);
							})
						) : (
							<span className='opacity-60 text-sm '>Нет номеров</span>
						)}
					</div>
				</span>
			</div>
			<div className='flex justify-between'>
				<span className='flex gap-1 items-center'>
					<div className='w-[18px]'>
						<ContainerIcon size={16} className='opacity-40' />
					</div>
					<span className='text-sm hover:bg-accent cursor-pointer select-none' onClick={e => handleCopy(e, client.buildId)}>
						{client.buildId}
					</span>
				</span>
			</div>

			<div className='flex justify-between'>
				<span className='flex gap-1 items-center'>
					<div className='w-[18px]'>
						<LayoutGridIcon size={16} className='opacity-40' />
					</div>
					<span className='text-sm hover:bg-accent cursor-pointer select-none' onClick={e => handleCopy(e, client.sdkVersion)}>
						{client.sdkVersion}
					</span>
				</span>
			</div>

			<div className='absolute right-1 bottom-1 flex gap-1' onClick={e => e.stopPropagation()}>
				<SelectTgStatus client={client} />
			</div>
		</Pressable>
	);
});

export default MyClientCard;
